import {
  AdditiveBlending,
  CanvasTexture,
  Color,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshBasicNodeMaterial,
  PlaneGeometry,
  Scene,
  Shape,
  ShapeGeometry,
  type Texture,
} from 'three/webgpu';
import { floor, fract, hash, mix, mx_noise_float, smoothstep, uv, vec2, vec3, float, abs, max, texture, uniform, color as tslColor } from 'three/tsl';
import { Config } from '@src/core/Config';
import type { RoomState } from '@src/ecs/world';
import { RoomPhase } from '@src/ecs/world';
import { DoorKind, GATES, GateSide, HALF_H, HALF_W } from '@src/game/arena/RoomLayout';
import { floorThemeDef, themeForFloor, type ArenaArt } from '@src/graphics/ArenaArt';
import type { FloorThemeDef } from '@src/game/arena/FloorThemes';

const DOOR_COLORS: Readonly<Record<DoorKind, number>> = {
  [DoorKind.Combat]: 0xff3b3b,
  [DoorKind.Treasure]: 0xffd23a,
  [DoorKind.Hazard]: 0x7cff3a,
  [DoorKind.Exit]: 0x7af6ff,
  [DoorKind.Sealed]: 0x1a1c26,
  [DoorKind.Arena]: 0xff7a2a,
  [DoorKind.Gauntlet]: 0xd8b070,
  [DoorKind.Sanctum]: 0xff9ad0,
};

const CROSSFADE_SECONDS = 0.8;
/** Time for the portcullis to winch fully up after a room clear. */
const GATE_OPEN_SECONDS = 1.1;

/** Torch sconce positions along the walls (x, y, side). */
const TORCHES: ReadonlyArray<readonly [number, number, GateSide]> = [
  [-11, HALF_H, GateSide.North],
  [-5, HALF_H, GateSide.North],
  [5, HALF_H, GateSide.North],
  [11, HALF_H, GateSide.North],
  [-11, -HALF_H, GateSide.South],
  [-5, -HALF_H, GateSide.South],
  [5, -HALF_H, GateSide.South],
  [11, -HALF_H, GateSide.South],
  [HALF_W, 5, GateSide.East],
  [HALF_W, -5, GateSide.East],
  [-HALF_W, 5, GateSide.West],
  [-HALF_W, -5, GateSide.West],
];

/** Height/width of a loaded texture, or the fallback when the art is missing. */
function texAspect(tex: Texture | null, fallback: number): number {
  const img = tex?.image as { width?: number; height?: number } | undefined;
  if (!img || !img.width || !img.height) return fallback;
  return img.height / img.width;
}

function radialGlowTexture(): CanvasTexture {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const g = canvas.getContext('2d');
  if (g) {
    const grad = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(0.4, 'rgba(255,255,255,0.35)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, size, size);
  }
  const tex = new CanvasTexture(canvas);
  tex.needsUpdate = true;
  return tex;
}

/** Theme wash: tint gathers at the walls so the fight floor stays readable. */
function edgeFogTexture(): CanvasTexture {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const g = canvas.getContext('2d');
  if (g) {
    const grad = g.createRadialGradient(size / 2, size / 2, size * 0.22, size / 2, size / 2, size * 0.55);
    grad.addColorStop(0, 'rgba(255,255,255,0)');
    grad.addColorStop(1, 'rgba(255,255,255,1)');
    g.fillStyle = grad;
    g.fillRect(0, 0, size, size);
  }
  const tex = new CanvasTexture(canvas);
  tex.needsUpdate = true;
  return tex;
}

/**
 * Arena dressing: painted floor (crossfading between floor themes), walls with
 * torch sconces and flicker lights, gate arches that slam shut and glow by door
 * kind, sealed sides drawn as solid stone, and a pulsing rune circle overlay.
 * Falls back to procedural materials when the generated art is missing.
 */
export class ArenaScene {
  readonly root = new Group();
  /** Flame anchor positions (x, y) for the renderer's torch sprite pool. */
  readonly torchFlames: Float32Array;
  readonly torchCount = TORCHES.length;
  private readonly art: ArenaArt | null;
  private readonly gateGlowMaterials: MeshBasicMaterial[] = [];
  private readonly gateGlowMeshes: Mesh[] = [];
  private readonly archClosed: Mesh[] = [];
  private readonly archClosedMaterials: MeshBasicMaterial[] = [];
  private readonly archFrame: Mesh[] = [];
  /**
   * Per gate: true = North/South front arch (raise along wall-normal into the header).
   * false = East/West upright side doorway (raise along the wall toward the lintel).
   */
  private readonly gateFrontFacing = new Array<boolean>(GATES.length);
  /** Per gate: 0 bars fully down, 1 bars fully raised. Eased toward the room state each frame. */
  private readonly openness = new Float32Array(GATES.length);
  private readonly seals: Mesh[] = [];
  private readonly arrowMeshes: Mesh[] = [];
  private readonly arrowMaterials: MeshBasicMaterial[] = [];
  private readonly torchLights: MeshBasicMaterial[] = [];
  private readonly tmpColor = new Color();
  private readonly wallMat = new MeshBasicMaterial({ color: 0x1a1c26 });
  private readonly trimMat = new MeshBasicMaterial({ color: 0x3a2a6a });
  private surroundMat: MeshBasicMaterial | null = null;
  private readonly fogMat: MeshBasicMaterial;
  private readonly archFrameMaterials: MeshBasicMaterial[] = [];
  private floorA = 0;
  private floorB = 0;
  private blend = 0;
  private fading = false;
  private texA: ReturnType<typeof texture> | null = null;
  private texB: ReturnType<typeof texture> | null = null;
  private readonly blendUniform = uniform(0);
  private readonly runePulse = uniform(0);
  private readonly runeTint = uniform(new Color(floorThemeDef(1).rune));
  private lastTheme = -1;
  private readonly procTint = uniform(new Color(floorThemeDef(1).surround));
  private readonly procAccent = uniform(new Color(floorThemeDef(1).trim));

  constructor(scene: Scene, art: ArenaArt | null, guest = false) {
    this.art = art;
    this.fogMat = new MeshBasicMaterial({
      map: edgeFogTexture(),
      transparent: true,
      depthWrite: false,
      blending: AdditiveBlending,
      color: floorThemeDef(1).fog,
      opacity: floorThemeDef(1).fogOpacity,
    });
    this.torchFlames = new Float32Array(TORCHES.length * 2);
    // Start raised so the very first room gets its slam too.
    this.openness.fill(guest ? 0 : 1);
    scene.add(this.root);
    this.buildFloor(guest);
    this.buildRune();
    this.buildWalls();
    this.buildTorches();
    this.buildGates();
    this.buildFog();
  }

  setOffset(x: number, y: number): void {
    this.root.position.set(x, y, 0);
  }

  /** Raise every gate so the cold-open slam drops from open, not already shut. */
  primeSlam(): void {
    this.openness.fill(1);
  }

  dispose(scene: Scene): void {
    scene.remove(this.root);
  }

  private buildFloor(guest: boolean): void {
    const geo = new PlaneGeometry(Config.room.width, Config.room.height);
    const mat = new MeshBasicNodeMaterial();
    const vignette = smoothstep(1.6, 0.45, max(abs(uv().x.sub(0.5).mul(2.4)), abs(uv().y.sub(0.5).mul(2.4))));
    if (this.art && this.art.floors.length > 0) {
      this.texA = texture(this.art.floors[0]);
      this.texB = texture(this.art.floors[0]);
      const painted = mix(this.texA.rgb, this.texB.rgb, this.blendUniform);
      mat.colorNode = painted.mul(vignette.mul(0.55).add(0.45));
    } else {
      // Theme-tinted flagstones when painted art is missing entirely.
      const tiles = uv().mul(vec2(19, 12));
      const cell = floor(tiles);
      const f = fract(tiles);
      const tileShade = hash(cell.x.add(cell.y.mul(57))).mul(0.16).add(0.84);
      const grime = mx_noise_float(vec3(uv().mul(6), float(2.5))).mul(0.1);
      const mortar = smoothstep(0.0, 0.06, f.x).mul(smoothstep(0.0, 0.06, f.y)).mul(smoothstep(1.0, 0.94, f.x)).mul(smoothstep(1.0, 0.94, f.y));
      const base = tslColor(this.procTint);
      const accent = tslColor(this.procAccent);
      const lit = base.mul(tileShade).add(grime);
      mat.colorNode = mix(accent.mul(0.25), lit, mortar).mul(vignette.mul(0.7).add(0.3));
    }
    const mesh = new Mesh(geo, mat);
    mesh.position.z = 0;
    mesh.renderOrder = 0;
    this.root.add(mesh);

    // Dark surround so the letterbox area reads as void, not floor.
    // Guest rooms skip it so the outgoing room stays visible during a walk.
    if (!guest) {
      this.surroundMat = new MeshBasicMaterial({ color: floorThemeDef(1).surround });
      const surround = new Mesh(new PlaneGeometry(Config.room.width + 12, Config.room.height + 12), this.surroundMat);
      surround.position.z = -0.01;
      surround.renderOrder = -1;
      this.root.add(surround);
    }
  }

  private buildRune(): void {
    if (!this.art?.rune) return;
    const size = Config.room.height * 0.9;
    const mat = new MeshBasicNodeMaterial();
    mat.transparent = true;
    mat.depthWrite = false;
    mat.blending = AdditiveBlending;
    const mask = texture(this.art.rune);
    mat.colorNode = tslColor(this.runeTint);
    mat.opacityNode = mask.r.mul(this.runePulse);
    const mesh = new Mesh(new PlaneGeometry(size, size), mat);
    mesh.position.z = 0.01;
    mesh.renderOrder = 1;
    this.root.add(mesh);
  }

  private buildFog(): void {
    const fog = new Mesh(new PlaneGeometry(Config.room.width * 1.05, Config.room.height * 1.05), this.fogMat);
    fog.position.z = 0.015;
    fog.renderOrder = 1;
    this.root.add(fog);
  }

  private buildWalls(): void {
    const t = Config.room.wallThickness;
    const mat = this.wallMat;
    const trim = this.trimMat;
    const add = (w: number, h: number, x: number, y: number, m: MeshBasicMaterial, z: number, order: number): void => {
      const mesh = new Mesh(new PlaneGeometry(w, h), m);
      mesh.position.set(x, y, z);
      mesh.renderOrder = order;
      this.root.add(mesh);
    };
    // Leave a hole at each gate so an adjacent room is visible through the doorway.
    const gw = Config.room.gateWidth;
    const hSpan = HALF_W - gw / 2;
    const vSpan = HALF_H - gw / 2;
    add(hSpan, t, -HALF_W + hSpan / 2, HALF_H - t / 2, mat, 0.05, 2);
    add(hSpan, t, HALF_W - hSpan / 2, HALF_H - t / 2, mat, 0.05, 2);
    add(hSpan, t, -HALF_W + hSpan / 2, -HALF_H + t / 2, mat, 0.05, 2);
    add(hSpan, t, HALF_W - hSpan / 2, -HALF_H + t / 2, mat, 0.05, 2);
    add(t, vSpan, HALF_W - t / 2, HALF_H - vSpan / 2, mat, 0.05, 2);
    add(t, vSpan, HALF_W - t / 2, -HALF_H + vSpan / 2, mat, 0.05, 2);
    add(t, vSpan, -HALF_W + t / 2, HALF_H - vSpan / 2, mat, 0.05, 2);
    add(t, vSpan, -HALF_W + t / 2, -HALF_H + vSpan / 2, mat, 0.05, 2);
    // Inner neon trim, also split around the doorways.
    const tw = Config.room.width - 2 * t - gw;
    const th = Config.room.height - 2 * t - gw;
    add(tw / 2, 0.06, -(gw / 2 + tw / 4), HALF_H - t - 0.03, trim, 0.06, 3);
    add(tw / 2, 0.06, gw / 2 + tw / 4, HALF_H - t - 0.03, trim, 0.06, 3);
    add(tw / 2, 0.06, -(gw / 2 + tw / 4), -HALF_H + t + 0.03, trim, 0.06, 3);
    add(tw / 2, 0.06, gw / 2 + tw / 4, -HALF_H + t + 0.03, trim, 0.06, 3);
    add(0.06, th / 2, HALF_W - t - 0.03, gw / 2 + th / 4, trim, 0.06, 3);
    add(0.06, th / 2, HALF_W - t - 0.03, -(gw / 2 + th / 4), trim, 0.06, 3);
    add(0.06, th / 2, -HALF_W + t + 0.03, gw / 2 + th / 4, trim, 0.06, 3);
    add(0.06, th / 2, -HALF_W + t + 0.03, -(gw / 2 + th / 4), trim, 0.06, 3);
  }

  private buildTorches(): void {
    const glowTex = radialGlowTexture();
    const sconceTex = this.art?.torch ?? null;
    const t = Config.room.wallThickness;
    for (let i = 0; i < TORCHES.length; i++) {
      const [x, y, side] = TORCHES[i];
      // Pull the sconce onto the wall face and the flame just inside the room.
      const nx = side === GateSide.East ? 1 : side === GateSide.West ? -1 : 0;
      const ny = side === GateSide.North ? 1 : side === GateSide.South ? -1 : 0;
      const sx = x - nx * t * 0.5;
      const sy = y - ny * t * 0.5;
      this.torchFlames[i * 2] = x - nx * (t + 0.25);
      this.torchFlames[i * 2 + 1] = y - ny * (t + 0.25);

      if (sconceTex) {
        // Art is authored tall (mount at the top, fire toward the room). Size the
        // plane from the PNG so a 1:1 quad does not squash the sconce.
        const aspect = texAspect(sconceTex, 1);
        const h = Math.min(0.95 * aspect, 1.15);
        const w = h / aspect;
        const sconce = new Mesh(new PlaneGeometry(w, h), new MeshBasicMaterial({ map: sconceTex, transparent: true, depthWrite: false }));
        sconce.position.set(sx, sy, 0.07);
        sconce.rotation.z = Math.atan2(-ny, -nx) + Math.PI / 2;
        sconce.renderOrder = 4;
        this.root.add(sconce);
      }
      const lightMat = new MeshBasicMaterial({ map: glowTex, transparent: true, depthWrite: false, blending: AdditiveBlending, color: 0xff8a3a, opacity: 0.35 });
      const light = new Mesh(new PlaneGeometry(5, 5), lightMat);
      light.position.set(this.torchFlames[i * 2] - nx * 0.6, this.torchFlames[i * 2 + 1] - ny * 0.6, 0.03);
      light.renderOrder = 3;
      this.root.add(light);
      this.torchLights.push(lightMat);
    }
  }

  private buildGates(): void {
    const arrowShape = new Shape();
    arrowShape.moveTo(-0.5, -0.5);
    arrowShape.lineTo(0.5, 0);
    arrowShape.lineTo(-0.5, 0.5);
    arrowShape.lineTo(-0.2, 0);
    arrowShape.closePath();
    const arrowGeo = new ShapeGeometry(arrowShape);
    const wallMat = this.wallMat;
    const t = Config.room.wallThickness;
    const alongWall = Config.room.gateWidth + 1.4;

    for (let i = 0; i < GATES.length; i++) {
      const g = GATES[i];
      const frontFacing = g.ny !== 0; // North/South use the front arch; East/West use upright side doors
      this.gateFrontFacing[i] = frontFacing;

      const gm = new MeshBasicMaterial({ color: 0x4b5566, transparent: true, opacity: 1 });
      const glow = new Mesh(new PlaneGeometry(g.hw * 2, g.hh * 2), gm);
      glow.position.set(g.x, g.y, 0.08);
      glow.renderOrder = 4;
      this.root.add(glow);
      this.gateGlowMeshes.push(glow);
      this.gateGlowMaterials.push(gm);

      const seal = new Mesh(new PlaneGeometry(g.hw * 2 + 0.1, g.hh * 2 + 0.1), wallMat);
      seal.position.set(g.x, g.y, 0.085);
      seal.renderOrder = 4;
      seal.visible = false;
      this.root.add(seal);
      this.seals.push(seal);

      // Frame draws on top of closed so the bars slide up behind the lintel/header.
      let closed: Mesh;
      let frame: Mesh;
      if (frontFacing) {
        // Front arch art is horizontal; rotate so local +Y (header) points into the room.
        const along = Math.atan2(g.ny, g.nx) + Math.PI / 2;
        const closedTex = this.art?.gateClosed ?? null;
        const frameTex = this.art?.gateFrame ?? null;
        const closedH = alongWall * texAspect(closedTex, 0.4);
        const frameH = alongWall * texAspect(frameTex, 0.4);
        closed = this.archMesh(closedTex, alongWall, closedH, 5);
        closed.position.set(g.x + g.nx * (t / 2 - closedH / 2), g.y + g.ny * (t / 2 - closedH / 2), 0.1);
        closed.rotation.z = along;
        frame = this.archMesh(frameTex, alongWall, frameH, 6);
        frame.position.set(g.x + g.nx * (t / 2 - frameH / 2), g.y + g.ny * (t / 2 - frameH / 2), 0.105);
        frame.rotation.z = along;
      } else {
        // Side doorway stays upright: bars remain vertical on screen. Plane X = into
        // the room, plane Y = along the wall. Fall back to front art if side art is missing.
        const closedTex = this.art?.gateSideClosed ?? this.art?.gateClosed ?? null;
        const frameTex = this.art?.gateSideFrame ?? this.art?.gateFrame ?? null;
        const aspect = texAspect(closedTex, 1.35);
        // Keep side doors shallow against the wall so they read as a cutout, not a floor rug.
        const intoRoom = Math.min(alongWall / Math.max(aspect, 0.75), Config.room.wallThickness + 1.8);
        const frameInto = Math.min(alongWall / Math.max(texAspect(frameTex, aspect), 0.75), Config.room.wallThickness + 1.8);
        closed = this.archMesh(closedTex, intoRoom, alongWall, 5);
        frame = this.archMesh(frameTex, frameInto, alongWall, 6);
        // East: wall on +X, opening faces west into the room. West: mirror so the
        // wall edge of the sprite sits on the west wall.
        const mirror = g.nx < 0 ? -1 : 1;
        closed.scale.x = mirror;
        frame.scale.x = mirror;
        closed.position.set(g.x + g.nx * (t / 2 - intoRoom / 2), g.y, 0.1);
        frame.position.set(g.x + g.nx * (t / 2 - frameInto / 2), g.y, 0.105);
      }
      this.root.add(closed);
      this.archClosed.push(closed);
      this.archClosedMaterials.push(closed.material as MeshBasicMaterial);
      this.root.add(frame);
      this.archFrame.push(frame);
      this.archFrameMaterials.push(frame.material as MeshBasicMaterial);

      const am = new MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0 });
      const arrow = new Mesh(arrowGeo, am);
      arrow.position.set(g.x - g.nx * 2.2, g.y - g.ny * 2.2, 0.11);
      arrow.rotation.z = Math.atan2(g.ny, g.nx);
      arrow.scale.setScalar(1.1);
      arrow.renderOrder = 6;
      this.root.add(arrow);
      this.arrowMeshes.push(arrow);
      this.arrowMaterials.push(am);
    }
  }

  private archMesh(tex: Texture | null, w: number, h: number, renderOrder: number): Mesh {
    const mat = tex
      ? new MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false })
      : new MeshBasicMaterial({ color: 0x2a2438, transparent: true, opacity: 0.9 });
    const mesh = new Mesh(new PlaneGeometry(w, h), mat);
    mesh.renderOrder = renderOrder;
    mesh.visible = tex !== null;
    return mesh;
  }

  /** Per-frame: theme crossfade, rune pulse, torch flicker, gate state. */
  update(room: Pick<RoomState, 'tier' | 'phase' | 'gatesOpen' | 'doorKinds'>, slam: number, t: number, dt: number, sharedOpen = -1, floor = 1): void {
    this.updateTheme(floor, dt);
    const pulse = 0.08 + 0.05 * Math.sin(t * 1.7) + (room.phase === RoomPhase.Clear ? 0.12 : 0);
    this.runePulse.value = pulse;
    for (let i = 0; i < this.torchLights.length; i++) {
      this.torchLights[i].opacity = 0.28 + 0.1 * Math.sin(t * 9 + i * 1.7) + 0.06 * Math.sin(t * 23 + i * 3.1);
    }
    this.updateGates(room.gatesOpen, room.doorKinds, slam, t, dt, sharedOpen);
  }

  private applyDressing(theme: FloorThemeDef): void {
    this.wallMat.color.setHex(theme.wall);
    this.trimMat.color.setHex(theme.trim);
    this.surroundMat?.color.setHex(theme.surround);
    this.fogMat.color.setHex(theme.fog);
    this.fogMat.opacity = theme.fogOpacity;
    this.tmpColor.setHex(theme.rune);
    (this.runeTint.value as Color).copy(this.tmpColor);
    this.tmpColor.setHex(theme.surround);
    (this.procTint.value as Color).copy(this.tmpColor);
    this.tmpColor.setHex(theme.trim);
    (this.procAccent.value as Color).copy(this.tmpColor);
    this.tmpColor.setHex(theme.torch);
    for (const l of this.torchLights) l.color.copy(this.tmpColor);
    this.tmpColor.setHex(theme.gate);
    for (const m of this.archClosedMaterials) m.color.copy(this.tmpColor);
    for (const m of this.archFrameMaterials) m.color.copy(this.tmpColor);
  }

  private updateTheme(floor: number, dt: number): void {
    const theme = floorThemeDef(floor);
    const floors = this.art?.floors ?? [];
    const target = floors.length > 0 ? themeForFloor(floor) % floors.length : themeForFloor(floor);
    if (target !== this.lastTheme) {
      const snap = this.lastTheme < 0;
      this.lastTheme = target;
      this.applyDressing(theme);
      if (this.texA && this.texB && floors.length > 0) {
        if (this.fading) {
          this.floorA = this.floorB;
          this.texA.value = floors[this.floorA];
        }
        this.floorB = target;
        this.texB.value = floors[this.floorB];
        if (snap || this.floorA === this.floorB) {
          this.floorA = target;
          this.texA.value = floors[target];
          this.blend = 0;
          this.fading = false;
          this.blendUniform.value = 0;
        } else {
          this.blend = 0;
          this.fading = true;
        }
      }
    }
    if (!this.fading) return;
    this.blend = Math.min(1, this.blend + dt / CROSSFADE_SECONDS);
    this.blendUniform.value = this.blend;
    if (this.blend >= 1) {
      this.fading = false;
      this.floorA = this.floorB;
      if (this.texA && floors.length > 0) this.texA.value = floors[this.floorA];
      this.blendUniform.value = 0;
    }
  }

  /**
   * Gates slam shut on entry and winch open on clear. `slam` in [0,1] drives the
   * bars dropping; opening eases over GATE_OPEN_SECONDS with the portcullis sliding
   * up behind the header stone of the frame layer. Sealed sides are solid wall.
   */
  private updateGates(open: boolean, doorKinds: Uint8Array, slam: number, t: number, dt: number, sharedOpen = -1): void {
    const hasFront = !!this.art?.gateClosed;
    const hasSide = !!(this.art?.gateSideClosed ?? this.art?.gateClosed);
    const archT = Config.room.wallThickness;
    for (let i = 0; i < GATES.length; i++) {
      const g = GATES[i];
      const frontFacing = this.gateFrontFacing[i];
      const hasArt = frontFacing ? hasFront : hasSide;
      const gm = this.gateGlowMaterials[i];
      const glow = this.gateGlowMeshes[i];
      const am = this.arrowMaterials[i];
      const arrow = this.arrowMeshes[i];
      const kind = doorKinds[i] as DoorKind;
      // Shared walk doorway stays a hole even if the incoming floor seals that edge
      // (floor-exit rebase). Otherwise the next room is hidden behind stone.
      const sealed = kind === DoorKind.Sealed && i !== sharedOpen;
      this.seals[i].visible = sealed;
      if (sealed) {
        glow.visible = false;
        this.archClosed[i].visible = false;
        this.archFrame[i].visible = false;
        am.opacity = 0;
        this.openness[i] = open ? 1 : 0;
        continue;
      }
      glow.visible = true;
      this.archFrame[i].visible = hasArt;

      // Ease the raw openness toward the room state; slamming shut overrides the ease.
      // A shared doorway during a walk stays winched up so you can see the next room.
      let o = this.openness[i];
      if (i === sharedOpen) o = 1;
      else if (open) o = Math.min(1, o + dt / GATE_OPEN_SECONDS);
      else o = Math.min(o, 1 - Math.min(1, slam));
      this.openness[i] = o;
      // Winch curve: slow start, steady pull, gentle settle.
      const v = o * o * (3 - 2 * o);

      // Glow behind the bars: dark iron when shut, door color once the way is clear.
      const pulse = 0.65 + 0.35 * Math.sin(t * 6 + i);
      this.tmpColor.setHex(DOOR_COLORS[kind]);
      gm.color.setHex(0x5a6474).lerp(this.tmpColor, v);
      gm.opacity = (hasArt ? 0.6 : 1) * (1 - v) + (0.35 + pulse * 0.4) * v;
      // The glow strip drops in with the bars on entry.
      const strip = open ? 1 : Math.min(1, slam);
      glow.scale.set(g.nx !== 0 ? 1 : strip, g.ny !== 0 ? 1 : strip, 1);

      // Exit arrows fade in once the bars are mostly up.
      const arrowIn = Math.max(0, (v - 0.6) / 0.4);
      am.color.copy(this.tmpColor);
      am.opacity = pulse * arrowIn;
      const bounce = Math.sin(t * 5 + i) * 0.25;
      arrow.position.set(g.x - g.nx * (2.2 - bounce), g.y - g.ny * (2.2 - bounce), 0.11);

      if (!hasArt) {
        this.archClosed[i].visible = false;
        continue;
      }
      // Bars compress toward the lintel/header and fade so nothing pokes past the stone.
      const arch = this.archClosed[i];
      const mat = this.archClosedMaterials[i];
      const fullH = (arch.geometry as PlaneGeometry).parameters.height;
      const fullW = (arch.geometry as PlaneGeometry).parameters.width;
      const s = 1 - 0.62 * v;
      const fade = Math.max(0, (v - 0.7) / 0.3);
      mat.opacity = 1 - fade;
      arch.visible = v < 0.999;

      if (frontFacing) {
        // Local +Y (texture up / header) points into the room: -normal.
        const ux = -g.nx;
        const uy = -g.ny;
        const topX = g.x + g.nx * (archT / 2) + ux * fullH;
        const topY = g.y + g.ny * (archT / 2) + uy * fullH;
        arch.position.set(topX - (ux * fullH * s) / 2, topY - (uy * fullH * s) / 2, 0.1);
        arch.scale.set(1, s, 1);
      } else {
        // Upright side door: raise along local Y (along the wall) toward the lintel at +Y.
        // Keep mirrored X scale for west; shrink Y only.
        const mirror = g.nx < 0 ? -1 : 1;
        const topY = g.y + fullH / 2;
        arch.position.set(g.x + g.nx * (archT / 2 - fullW / 2), topY - (fullH * s) / 2, 0.1);
        arch.scale.set(mirror, s, 1);
      }
    }
  }
}
